import React, { useState } from 'react';
import { Button, Chip, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Assuming these icons are in the same directory as AiButton.tsx
// If not, adjust paths or consider passing them as props.
import notaireAssistantIcon from '../../../../assets/notaire-assistant.svg';
// import DefaultIcon from '../../../../assets/NotaireAssistantIcon.svg';

export type AiButtonVariant = 'default' | 'hovered' | 'pressed' | 'disabled';

interface AiButtonProps {
  variant?: AiButtonVariant;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  text?: string;
}

const StyledButtonRoot = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'componentvariant',
})<{ componentvariant: AiButtonVariant }>(({ theme, componentvariant }) => {
  // Base styles for 'default' variant and common properties
  const styles: any = {
    position: 'relative',
    borderRadius: '50px',
    padding: '12px 28px', // Default padding
    textTransform: 'none',
    color: 'white',
    fontWeight: 'bold',
    lineHeight: 'normal',
    // background: 'linear-gradient(to right, #6E2DFA, #935FF9)', // Default background
    background: "#3660F4",
    transform: 'none', // Default transform
    transition: 'background 0.3s ease, transform 0.1s ease',
    // Default interaction styles (for 'default' variant)
    // '&:hover': {
    //   background: 'linear-gradient(to right, #3B82F6, #8B5CF6)',
    // },
    '&:active': {
      background: 'linear-gradient(to right, #1E44B6, #FF6136)',
      transform: 'scale(0.98)',
    },
  };

  if (componentvariant === 'disabled') {
    styles.background = 'linear-gradient(to right, #D1D9F6, #E6E1FB)';
    styles.color = theme.palette.action.disabled;
    styles.cursor = 'not-allowed';
    styles.transform = 'none'; // Ensure no transform for disabled
    styles['&:hover'] = { // Actual hover on 'disabled' variant
      background: 'linear-gradient(to right, #D1D9F6, #E6E1FB)',
      transform: 'none',
    };
    styles['&:active'] = { // Actual active on 'disabled' variant
      background: 'linear-gradient(to right, #D1D9F6, #E6E1FB)',
      transform: 'none',
    };
  }
  // If componentvariant is 'default', the initial styles and their '&:hover', '&:active' are used.

  return styles;
});

const NouveauBadge = styled(Chip)({
  position: 'absolute',
  top: '-6px',
  left: '-13px',
  backgroundColor: '#FF9B56', // Orange color from image
  color: 'white',
  height: '18px',
  width: '48px',
  fontSize: '10px',
  fontWeight: '700',
  borderRadius: '7px',
  '.MuiChip-label': {
    paddingLeft: '2px',
    paddingRight: '2px',
  },
  zIndex: 1,
});

const ButtonContentWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%', // Ensure content centers properly
});

const IconWrapper = styled(Box)({
  width: '24px',
  height: '24px',
  marginRight: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    width: '100%',
    height: '100%',
  },
});

const ButtonText = styled(Typography)(() => ({ // Changed prop name to avoid conflict
  fontWeight: 'semibold',
  fontSize: '1rem',
  lineHeight: 'normal',
}));

export const AiButton: React.FC<AiButtonProps> = ({
  variant = 'default',
  className,
  onClick,
  text = "Assistant IA",
}) => {

  const isDisabled = variant === 'disabled';

  const currentIconPath = notaireAssistantIcon;

  return (
    <StyledButtonRoot
      className={className}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      componentvariant={variant}
      aria-disabled={isDisabled}
    >
      {/* <NouveauBadge label="Nouveau" /> */}

      <ButtonContentWrapper>
        <IconWrapper>
          <img src={currentIconPath} alt="Assistant Icon" />
        </IconWrapper>
        <ButtonText>
          {text}
        </ButtonText>
      </ButtonContentWrapper>
    </StyledButtonRoot>
  );
};
